import { useCallback, useState } from 'react';

import { useWallContentStore, type WallBoard } from '@walls/store';
import { getExportFilename } from './exportFilenames';
import { captureWallExport, getCanvasImageBlob } from './wallExportCapture';

export function useExportWallImage(board: WallBoard) {
  const [isExporting, setIsExporting] = useState(false);
  const cards = useWallContentStore((state) => state.cards);
  const connections = useWallContentStore((state) => state.connections);
  const shapes = useWallContentStore((state) => state.shapes);

  const exportWallImage = useCallback(async () => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);

    try {
      const { canvas } = await captureWallExport({
        board,
        cards,
        connections,
        shapes,
      });
      const imageBlob = await getCanvasImageBlob(canvas);
      const imageUrl = URL.createObjectURL(imageBlob);

      try {
        downloadUrl(imageUrl, getExportFilename(board.title, getImageExtension(imageBlob)));
      } finally {
        URL.revokeObjectURL(imageUrl);
      }
    } finally {
      setIsExporting(false);
    }
  }, [board, cards, connections, isExporting, shapes]);

  return { exportWallImage, isExporting };
}

function downloadUrl(url: string, filename: string) {
  const downloadLink = document.createElement('a');

  downloadLink.href = url;
  downloadLink.download = filename;
  downloadLink.style.display = 'none';
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();
}

function getImageExtension(blob: Blob) {
  return blob.type === 'image/jpeg' ? 'jpg' : 'png';
}
